/**
 * A direct port of:
 * https://github.com/Swizec/useDimensions/blob/master/src/index.js
 * However, this author exposes far more functionality, including scroll position.
 * We don't have a use case for that, so that was omitted from this hook.
 */
import { useCallback, useLayoutEffect, useState, useRef } from 'react'
import debounce from 'lodash/debounce'

interface Dimensions {
  height: number
  width: number
}

const getDimensions = (node: HTMLElement): Dimensions => {
  const { width, height } = node.getBoundingClientRect()

  return {
    width,
    height,
  }
}

/**
 * A React Hook which returns utils to listen to dynamic element size.
 *
 * @param delay - Optionally set a delay to debounce updates on resizing events. Defaults to 300ms.
 * @returns [setDimensionRef, dimensions, node]
 *   setDimensionRef is a callback effect that sets node to query for dimensions
 *   dimensions returns an object with width and height properties of
 *   node refers to the passed nodeToSet, returned for testing purposes
 */
const useResizeListener = (delay = 300): [() => void, Dimensions, HTMLElement | undefined] => {
  const [effectCancelled] = useState<boolean>(false)
  const resizeRef = useRef<number>()

  const [dimensions, setDimensions] = useState<Dimensions>({ width: 0, height: 0 })
  const [node, setNode] = useState<HTMLElement | undefined>(undefined)

  const setDimensionRef = useCallback((nodeToSet?: HTMLElement | undefined): void => {
    if (nodeToSet) {
      setNode(nodeToSet)
    }
  }, [])

  useLayoutEffect(() => {
    if (node) {
      const measure = () => {
        resizeRef.current = window.requestAnimationFrame(() => {
          setDimensions(getDimensions(node))
        })
      }
      const measureWithDebounce = debounce(measure, delay)

      measure()

      window.addEventListener('resize', measureWithDebounce)

      return () => {
        // Cancel animation frame request if there is one pending
        if (resizeRef.current) cancelAnimationFrame(resizeRef.current)
        // Cancel debounce
        measureWithDebounce.cancel()
        // Remove the window event listener
        window.removeEventListener('resize', measureWithDebounce)
      }
    }

    return undefined
  }, [delay, effectCancelled, node])

  return [setDimensionRef, dimensions, node]
}

export default useResizeListener
